// SMSF Error Boundary - Graceful error handling for SMSF Calculator
// Prevents crashes and provides user-friendly error messages

import React from 'react';

class SMSFErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorCount: 0
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log error details
        console.error('SMSF Error Boundary caught an error:', error, errorInfo);
        
        // Update state with error details
        this.setState(prevState => ({
            error,
            errorInfo,
            errorCount: prevState.errorCount + 1
        }));

        // Send error to logging service (if configured)
        this.logErrorToService(error, errorInfo);
    }

    logErrorToService(error, errorInfo) {
        // This could send to an external logging service
        const errorData = {
            timestamp: new Date().toISOString(),
            message: error.toString(),
            stack: error.stack,
            componentStack: errorInfo.componentStack,
            userAgent: navigator.userAgent,
            url: window.location.href,
            component: this.props.componentName || 'Unknown'
        };

        // Store in localStorage for debugging
        try {
            const existingErrors = JSON.parse(localStorage.getItem('smsf_errors') || '[]');
            existingErrors.push(errorData);
            // Keep last 10 errors only
            const recentErrors = existingErrors.slice(-10);
            localStorage.setItem('smsf_errors', JSON.stringify(recentErrors));
        } catch (e) {
            console.error('Failed to log error:', e);
        }

        // Could also send to external service
        // fetch('/api/log-error', { method: 'POST', body: JSON.stringify(errorData) });
    }

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            const { fallback, componentName, allowReset = true } = this.props;
            
            // Use custom fallback if provided
            if (fallback) {
                return typeof fallback === 'function' 
                    ? fallback(this.state.error, this.handleReset)
                    : fallback;
            }

            // Default error UI
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
                    <div className="max-w-2xl w-full">
                        <div className="bg-white rounded-lg shadow-lg p-8">
                            {/* Error Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                                    <svg className="w-12 h-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                            </div>

                            {/* Error Message */}
                            <div className="text-center mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    Something Went Wrong
                                </h2>
                                <p className="text-gray-600">
                                    {componentName 
                                        ? `An error occurred in the ${componentName} component.`
                                        : 'An unexpected error occurred in the SMSF Calculator.'
                                    }
                                </p>
                                <p className="text-sm text-gray-500 mt-2">
                                    Don't worry - your data is safe and hasn't been lost.
                                </p>
                            </div>

                            {/* Error Details (Collapsible) */}
                            {typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development' && this.state.error && (
                                <details className="mb-6 bg-gray-50 rounded-lg p-4">
                                    <summary className="cursor-pointer font-medium text-gray-700 mb-2">
                                        Technical Details (Development Only)
                                    </summary>
                                    <div className="mt-2 text-sm text-gray-600 space-y-2">
                                        <div>
                                            <strong>Error:</strong>
                                            <pre className="mt-1 p-2 bg-red-50 rounded text-xs overflow-auto">
                                                {this.state.error.toString()}
                                            </pre>
                                        </div>
                                        {this.state.errorInfo && (
                                            <div>
                                                <strong>Component Stack:</strong>
                                                <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-40">
                                                    {this.state.errorInfo.componentStack}
                                                </pre>
                                            </div>
                                        )}
                                        <div className="text-xs text-gray-500">
                                            Error #{this.state.errorCount} in this session
                                        </div>
                                    </div>
                                </details>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                {allowReset && (
                                    <button
                                        onClick={this.handleReset}
                                        className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                    >
                                        Try Again
                                    </button>
                                )}
                                <button
                                    onClick={this.handleReload}
                                    className="px-6 py-3 bg-gray-600 text-white font-medium rounded-md hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                >
                                    Reload Page
                                </button>
                                <button
                                    onClick={() => window.history.back()}
                                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    Go Back
                                </button>
                            </div>

                            {/* Help Text */}
                            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                                <h3 className="font-semibold text-blue-900 mb-2">What you can do:</h3>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    <li>• Click "Try Again" to retry the operation</li>
                                    <li>• Click "Reload Page" to start fresh</li>
                                    <li>• Click "Go Back" to return to the previous screen</li>
                                    <li>• Check your internet connection</li>
                                    <li>• Clear your browser cache if the problem persists</li>
                                </ul>
                            </div>

                            {/* Support Contact */}
                            <div className="mt-6 text-center text-sm text-gray-500">
                                <p>If this error persists, please contact support with error details.</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Functional wrapper for easier use
export const withErrorBoundary = (Component, componentName) => {
    return (props) => (
        <SMSFErrorBoundary componentName={componentName}>
            <Component {...props} />
        </SMSFErrorBoundary>
    );
};

// Lightweight error boundary for non-critical components
export class LightweightErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Lightweight Error Boundary:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center mb-2">
                        <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium text-red-800">
                            {this.props.message || 'This component encountered an error'}
                        </span>
                    </div>
                    <p className="text-sm text-red-700">
                        The rest of the calculator is still working normally.
                    </p>
                    {this.props.onRetry && (
                        <button
                            onClick={() => this.setState({ hasError: false })}
                            className="mt-3 px-4 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                        >
                            Retry
                        </button>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}

export default SMSFErrorBoundary;
